import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { Capacitor } from "@capacitor/core";
import { db } from "../firebase";
import { C } from "../theme";
import Button from "../components/Button";
import Card from "../components/Card";
import CollapsibleSection from "../components/CollapsibleSection";
import VoiceWave from "../components/VoiceWave";
import { useSessionContext } from "../context/SessionContext";
import useTideStage from "../hooks/useTideStage";
import useMoonPhase from "../hooks/useMoonPhase";
import useSolunarMajor from "../hooks/useSolunarMajor";
import useWaterTemp from "../hooks/useWaterTemp";
import useWind from "../hooks/useWind";
import useBarometricPressure from "../hooks/useBarometricPressure";
import useWaterClarityOptions from "../hooks/useWaterClarityOptions";
import useVoiceCommands from "../hooks/useVoiceCommands";
import useFlowStage from "../hooks/useFlowStage";
import { buildConditionsSnapshot } from "../utils/conditionsSnapshot";
import { extractCatchFromTranscript } from "../utils/extractCatchFromTranscript";
import { useAnglerCatalog, findById } from "../data/anglerCatalog";
import { canUseVoiceCatchLogging, isProActive } from "../utils/featureGate";
import { authedWrite } from "../utils/authedWrite";
import { getActiveWaterType } from "../utils/waterType";
import { suggestClarityFromTurbidity } from "../utils/waterClaritySuggest";

function formatGps(location) {
  if (!location?.lat || !location?.lng) return "GPS unavailable";
  const lat = Math.abs(location.lat).toFixed(3) + "° " + (location.lat >= 0 ? "N" : "S");
  const lng = Math.abs(location.lng).toFixed(3) + "° " + (location.lng >= 0 ? "E" : "W");
  return `${lat} · ${lng}`;
}

function formatLength(lengthIn) {
  return typeof lengthIn === "number" ? `${lengthIn.toFixed(1).replace(/\.0$/, "")}"` : "—";
}

function formatWeight(weightLbs) {
  return typeof weightLbs === "number" ? `${weightLbs.toFixed(1).replace(/\.0$/, "")} lbs` : "—";
}

// Compute the next value for a stepper, clamped to [min, max] and rounded to
// the step's decimal precision (avoids floating-point drift like 0.30000004).
function stepNumber(current, dir, { min, max, step }) {
  const parsed = Number(String(current ?? "").trim());
  const base = Number.isFinite(parsed) ? parsed : 0;
  const decimals = (String(step).split(".")[1] || "").length;
  let next = Number((base + dir * step).toFixed(decimals));
  if (typeof min === "number") next = Math.max(min, next);
  if (typeof max === "number") next = Math.min(max, next);
  return String(next);
}

// Themed numeric input with custom ▲/▼ steppers. The native browser spin
// buttons render inconsistently inside the Android WebView (default black
// arrows on a dark field), so we hide them and draw our own on-brand controls.
function ThemedNumberInput({
  id,
  testId,
  ariaLabel,
  value,
  min,
  max,
  step,
  hasError,
  suffix,
  onChange,
  onBlur,
  onStep,
}) {
  const stepBtnStyle = {
    flex: 1,
    width: 22,
    minHeight: 0,
    border: `1px solid ${C.seafoam}55`,
    background: `${C.seafoam}1f`,
    color: C.seafoam,
    borderRadius: 4,
    fontSize: 7,
    lineHeight: 1,
    padding: 0,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <div style={{ position: "relative", flex: 1, display: "flex", alignItems: "center" }}>
        <input
          id={id}
          data-testid={testId}
          className="manual-number-themed"
          type="number"
          inputMode="decimal"
          aria-label={ariaLabel}
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onBlur={onBlur}
          style={{
            width: "100%",
            height: 30,
            borderRadius: 8,
            border: `1px solid ${hasError ? C.red : C.border}`,
            background: C.cardBg,
            color: C.white,
            padding: "0 32px 0 8px",
            fontSize: 11,
            boxSizing: "border-box",
          }}
        />
        <div
          style={{
            position: "absolute",
            right: 4,
            top: 3,
            bottom: 3,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            width: 22,
          }}
        >
          <button type="button" tabIndex={-1} aria-label="Increase" onClick={() => onStep(1)} style={stepBtnStyle}>
            ▲
          </button>
          <button type="button" tabIndex={-1} aria-label="Decrease" onClick={() => onStep(-1)} style={stepBtnStyle}>
            ▼
          </button>
        </div>
      </div>
      {suffix ? <span style={{ fontSize: 11, color: C.white, fontWeight: 700 }}>{suffix}</span> : null}
    </div>
  );
}

function getSpeechRecognitionClass() {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

function isMobileUserAgent() {
  if (typeof navigator === "undefined") return false;
  return /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent || "");
}

export default function CatchScreen({ onClose, onSaved, location, pendingCertId, allowedSpecies, tournamentName, userProfile = null, initialOutcome = "landed" }) {
  const { logCatch, sessionId, userId } = useSessionContext();

  // Report a saved (landed) catch back to the host in the display shape
  // VideoScreen expects, so a catch logged before its video can be attached at
  // certify time.
  const emitSaved = React.useCallback((catchId, activeSessionId, fields) => {
    if (!onSaved || !catchId || !activeSessionId) return;
    const size = typeof fields.lengthIn === "number"
      ? `${fields.lengthIn.toFixed(1).replace(/\.0$/, "")}"`
      : "—";
    const weight = typeof fields.weightLbs === "number"
      ? `${fields.weightLbs.toFixed(1).replace(/\.0$/, "")} lbs`
      : "—";
    onSaved({
      catchId,
      sessionId: activeSessionId,
      catchData: {
        species: fields.species || "—",
        size,
        weight,
        lure: fields.lure || "—",
      },
    });
  }, [onSaved]);

  const backfillCert = React.useCallback(async (catchId, activeSessionId, catchData) => {
    if (!pendingCertId || !catchId || !activeSessionId) return;
    const catchRef = { userId, sessionId: activeSessionId, catchId };
    // Denormalize catch fields onto the video doc so the GoPro Integration
    // screen can render species / length / weight without a second hop.
    const denorm = catchData
      ? {
          species: catchData.species ?? null,
          lengthIn: typeof catchData.lengthIn === "number" ? catchData.lengthIn : null,
          weightLbs: typeof catchData.weightLbs === "number" ? catchData.weightLbs : null,
          lure: catchData.lure ?? null,
          liveBait: catchData.liveBait ?? null,
        }
      : {};
    try {
      await authedWrite(() => setDoc(doc(db, "certificates", pendingCertId), { catchRef, ...denorm }, { merge: true }));
      await authedWrite(() => setDoc(doc(db, "videos", pendingCertId), { catchRef, ...denorm }, { merge: true }));
      await authedWrite(() =>
        setDoc(
          doc(db, "users", userId, "sessions", activeSessionId, "catches", catchId),
          {
            status: "certified",
            certificateId: pendingCertId,
            videoCertified: true,
            certifiedAt: serverTimestamp(),
          },
          { merge: true }
        )
      );
    } catch (e) {
      console.error("[CatchScreen] backfill cert failed (non-fatal):", e);
    }
  }, [pendingCertId, userId]);
  const tide = useTideStage({
    lat: location?.lat,
    lng: location?.lng,
    enabled: !!location,
  });
  const moon = useMoonPhase(location);
  const solunarMajor = useSolunarMajor(location);
  const waterTemp = useWaterTemp(location, "F");
  const wind = useWind(location);
  const barometric = useBarometricPressure(location);

  const [phase, setPhase] = useState("listening");
  const [isListening, setIsListening] = useState(false);
  const [text, setText] = useState("");
  const [parsed, setParsed] = useState({ species: null, lengthIn: null, weightLbs: null, lure: null });
  const [voiceError, setVoiceError] = useState(null);
  const [micPermissionGranted, setMicPermissionGranted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [showManualEntry, setShowManualEntry] = useState(true);
  // "landed" = fish brought to hand (default); "missed" = a bite/strike that
  // got away. Missed fish reuse species/lure/bait/clarity but omit length &
  // weight. A caller (e.g. RecordingScreen's "Missed fish" button) can open
  // the sheet pre-set to missed mode.
  const [catchOutcome, setCatchOutcome] = useState(initialOutcome === "missed" ? "missed" : "landed");
  const isMissed = catchOutcome === "missed";
  const [manualSpecies, setManualSpecies] = useState("");
  const [manualLength, setManualLength] = useState("");
  const [manualLure, setManualLure] = useState("");
  const [manualLiveBait, setManualLiveBait] = useState("");
  const [manualWeightPounds, setManualWeightPounds] = useState("");
  const [manualWeightOunces, setManualWeightOunces] = useState("");
  const [waterClarity, setWaterClarity] = useState("");
  const [manualFieldErrors, setManualFieldErrors] = useState({
    species: "",
    length: "",
    lure: "",
    pounds: "",
    ounces: "",
  });

  const recognitionRef = useRef(null);
  const finalTranscriptRef = useRef("");
  const lastInterimRef = useRef("");
  const shouldFinalizeRef = useRef(false);
  const silenceTimerRef = useRef(null);
  const maxDurationTimerRef = useRef(null);
  // Once the angler explicitly picks (or clears) a lure / live bait, stop
  // auto-defaulting so clearing one actually sticks and frees up the other.
  const baitTouchedRef = useRef(false);
  const isNativeVoice = Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android";
  const isSpeechSupported = isNativeVoice || !!getSpeechRecognitionClass();
  const isMobileSpeech = !isNativeVoice && isMobileUserAgent();

  // Native dictation result handler. Stable callback so the hook keeps a fresh ref.
  const handleNativeDictationResult = useCallback((nativeParsed, raw, reason) => {
    setIsListening(false);
    if (reason === "cancelled") return;
    if (!raw || !raw.trim()) {
      setVoiceError("No speech detected — try again");
      return;
    }
    setText(raw);
    setParsed({
      species: nativeParsed?.species ?? null,
      lengthIn: nativeParsed?.lengthIn ?? null,
      weightLbs: nativeParsed?.weightLbs ?? null,
      lure: nativeParsed?.lure ?? null,
    });
    setPhase("confirming");
  }, []);

  const voice = useVoiceCommands({ onDictationResult: handleNativeDictationResult });

  const catchConditions = useMemo(
    () =>
      buildConditionsSnapshot({
        moon,
        solunarMajor,
        waterTemp,
        wind,
        barometric,
        tideStage: tide.tideStage,
        nextTide: tide.nextTide,
      }),
    [moon, solunarMajor, waterTemp, wind, barometric, tide.tideStage, tide.nextTide]
  );

  const isTournamentSpeciesRestricted =
    Array.isArray(allowedSpecies) && allowedSpecies.length > 0;

  // Load the angler's preferred species (only when not in a tournament).
  // Stored as fishSpecies doc-ID array in userPreferences/{uid}; resolved
  // to display labels via the shared fishSpecies catalog.
  const { species: speciesCatalog, lures: lureCatalog } = useAnglerCatalog();
  // Angler's curated picks live in userPreferences/{uid} as catalog-ID arrays
  // (fishSpeciesIds / lureIds / liveBaitIds). Free anglers have a single pick
  // each (seeded at enrollment); Pro anglers curate fuller lists on the
  // dashboard. Loaded only when not inside a species-restricted tournament.
  const [userPreferredSpeciesIds, setUserPreferredSpeciesIds] = useState([]);
  const [userPreferredLureIds, setUserPreferredLureIds] = useState([]);
  const [userPreferredLiveBaitIds, setUserPreferredLiveBaitIds] = useState([]);
  useEffect(() => {
    if (isTournamentSpeciesRestricted) return undefined;
    if (!userId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "userPreferences", userId));
        if (cancelled) return;
        const data = snap.exists() ? (snap.data() || {}) : {};
        const toIds = (arr) =>
          Array.isArray(arr) ? arr.map((id) => String(id || "").trim()).filter(Boolean) : [];
        setUserPreferredSpeciesIds(toIds(data.fishSpeciesIds));
        setUserPreferredLureIds(toIds(data.lureIds));
        setUserPreferredLiveBaitIds(toIds(data.liveBaitIds));
      } catch (e) {
        console.warn("[CatchScreen] failed to load userPreferences:", e?.message || e);
      }
    })();
    return () => { cancelled = true; };
  }, [userId, isTournamentSpeciesRestricted]);
  const normalizedAllowedSpecies = useMemo(
    () =>
      isTournamentSpeciesRestricted
        ? allowedSpecies.map((s) => String(s || "").trim().toLowerCase()).filter(Boolean)
        : [],
    [allowedSpecies, isTournamentSpeciesRestricted]
  );
  const isSpeciesAllowed = (value) => {
    if (!isTournamentSpeciesRestricted) return true;
    const normalized = String(value || "").trim().toLowerCase();
    if (!normalized) return false;
    return normalizedAllowedSpecies.includes(normalized);
  };
  const isHarness = process.env.REACT_APP_USE_EMULATORS === "true";
  // Voice catch logging is a Pro feature (subscriptions.md §3a;
  // featureGate.canUseVoiceCatchLogging). Free / Pass-only users get the
  // manual entry form only.
  const canVoiceLog = canUseVoiceCatchLogging(userProfile);
  // Pro anglers pick from their curated species/lure/live-bait lists; Free
  // anglers are locked to the single pick they made at enrollment.
  const isPro = isProActive(userProfile);
  const trialTrack = userProfile?.trialTrack || {};
  const trialSpeciesId = String(trialTrack?.species || "").trim();
  const trialLureId = String(trialTrack?.lure || "").trim();
  const trialLiveBaitId = String(trialTrack?.liveBait || "").trim();
  // Resolve a stored catalog ID to its display label. Falls back to a
  // token-normalized match so legacy / token-order-drifted slugs still map
  // (e.g. "gold_spoon" resolves against catalog id "spoon_gold").
  const resolveCatalogLabel = (catalog, id) => {
    if (!id) return "";
    const direct = findById(catalog, id);
    if (direct) return direct.label;
    const key = (s) => String(s || "").toLowerCase().split(/[_\s-]+/).filter(Boolean).sort().join("_");
    const target = key(id);
    const match = (Array.isArray(catalog) ? catalog : []).find((e) => key(e.id) === target);
    return match?.label || id;
  };
  const resolveLabels = (catalog, ids) =>
    (Array.isArray(ids) ? ids : [])
      .map((id) => resolveCatalogLabel(catalog, id))
      .filter(Boolean);
  const trialSpeciesLabel = resolveCatalogLabel(speciesCatalog, trialSpeciesId);
  const trialLureLabel = resolveCatalogLabel(lureCatalog, trialLureId);
  const trialLiveBaitLabel = resolveCatalogLabel(lureCatalog, trialLiveBaitId);

  // Species options: tournament list > Pro curated list > Free single pick.
  const speciesOptions = useMemo(() => {
    if (Array.isArray(allowedSpecies) && allowedSpecies.length > 0) {
      return allowedSpecies.map((s) => String(s || "").trim()).filter(Boolean);
    }
    if (isPro) {
      const pro = resolveLabels(speciesCatalog, userPreferredSpeciesIds);
      if (pro.length > 0) return Array.from(new Set(pro));
    }
    if (trialSpeciesLabel) return [trialSpeciesLabel];
    return ["Redfish", "Seatrout", "Flounder"];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedSpecies, isPro, userPreferredSpeciesIds, speciesCatalog, trialSpeciesLabel]);

  // Lure options: Pro curated lures > Free single lure pick (empty when the
  // Free angler tracks live bait instead, so the live-bait select carries it).
  const lureOptions = useMemo(() => {
    if (isPro) {
      const pro = resolveLabels(lureCatalog, userPreferredLureIds);
      if (pro.length > 0) return Array.from(new Set(pro));
    }
    if (trialLureLabel) return [trialLureLabel];
    if (trialLiveBaitLabel) return [];
    return ["Paddletail", "Topwater", "Subsurface", "Spoon"];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPro, userPreferredLureIds, lureCatalog, trialLureLabel, trialLiveBaitLabel]);

  // Live bait options: Pro curated live baits > Free single live-bait pick.
  const liveBaitOptions = useMemo(() => {
    if (isPro) {
      const pro = resolveLabels(lureCatalog, userPreferredLiveBaitIds);
      if (pro.length > 0) return Array.from(new Set(pro));
    }
    if (trialLiveBaitLabel) return [trialLiveBaitLabel];
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPro, userPreferredLiveBaitIds, lureCatalog, trialLiveBaitLabel]);
  const waterClarityOptions = useWaterClarityOptions();

  // ── Freshwater turbidity → water-clarity suggestion ─────────────────────
  // For freshwater trips, pull the nearest USGS gage's turbidity (63680, FNU)
  // and pre-fill the Water Clarity picker with the matching band. The angler
  // can always override; once they touch the control we stop auto-filling.
  const freshwaterActive = getActiveWaterType(userProfile) === "freshwater";
  const selectedGageId = useMemo(() => {
    try { return localStorage.getItem("the99.usgsSiteId") || undefined; } catch { return undefined; }
  }, []);
  const flow = useFlowStage({
    lat: location?.lat,
    lng: location?.lng,
    enabled: !!location && freshwaterActive,
    siteId: selectedGageId,
  });
  const suggestedClarity = useMemo(
    () => (freshwaterActive ? suggestClarityFromTurbidity(flow.turbidity, waterClarityOptions) : null),
    [freshwaterActive, flow.turbidity, waterClarityOptions]
  );
  const clarityTouchedRef = useRef(false);
  const setWaterClarityTouched = useCallback((value) => {
    clarityTouchedRef.current = true;
    setWaterClarity(value);
  }, []);
  useEffect(() => {
    if (clarityTouchedRef.current) return;
    if (!waterClarity && suggestedClarity) setWaterClarity(suggestedClarity);
  }, [suggestedClarity, waterClarity]);

  useEffect(() => {
    // In a tournament, keep the species picker on an eligible fish: default to
    // (or snap back to) the first tournament species when the current pick is
    // empty or not allowed. Outside a tournament, fall back to the trial pick.
    if (isTournamentSpeciesRestricted) {
      if (!manualSpecies || !isSpeciesAllowed(manualSpecies)) {
        const firstAllowed = speciesOptions[0];
        if (firstAllowed && firstAllowed !== manualSpecies) setManualSpecies(firstAllowed);
      }
      return;
    }
    if (!manualSpecies && trialSpeciesLabel) {
      setManualSpecies(trialSpeciesLabel);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualSpecies, trialSpeciesLabel, isTournamentSpeciesRestricted, speciesOptions]);

  useEffect(() => {
    // Lure and live bait are mutually exclusive, so default AT MOST ONE (never
    // both — pre-filling both would leave each control disabled by the other).
    // Only default when neither has been chosen yet AND the angler hasn't
    // explicitly touched either control (so clearing a selection sticks and
    // frees up the other for selection).
    if (baitTouchedRef.current) return;
    if (manualLure || manualLiveBait) return;
    if (lureOptions.length > 0) {
      setManualLure(lureOptions[0]);
    } else if (liveBaitOptions.length > 0) {
      setManualLiveBait(liveBaitOptions[0]);
    }
  }, [manualLure, manualLiveBait, lureOptions, liveBaitOptions]);

  useEffect(() => {
    if (phase !== "listening" || !isListening) return undefined;
    // Native dictation is driven entirely by the useVoiceCommands hook.
    if (isNativeVoice) return undefined;
    // Mobile recognition is started synchronously in handleStartListening to stay within the user gesture
    if (isMobileSpeech) return undefined;
    let disposed = false;

    if (!isSpeechSupported) {
      setVoiceError("Voice capture unavailable on this browser. Try Chrome or Edge on HTTPS/localhost.");
      setIsListening(false);
      return undefined;
    }

    setVoiceError(null);
    setText("");
    finalTranscriptRef.current = "";
    lastInterimRef.current = "";
    shouldFinalizeRef.current = false;

    const SpeechRecognitionCtor = getSpeechRecognitionClass();
    if (!SpeechRecognitionCtor) {
      setVoiceError("Voice capture unavailable on this browser. Try Chrome or Edge on HTTPS/localhost.");
      setIsListening(false);
      return undefined;
    }

    const recognition = new SpeechRecognitionCtor();
    recognition.lang = "en-US";
    recognition.interimResults = true;
    recognition.continuous = true;

    const clearSilenceTimer = () => {
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }
    };
    const armSilenceTimer = () => {
      clearSilenceTimer();
      // Finalize after 2s of no new speech results.
      silenceTimerRef.current = setTimeout(() => {
        shouldFinalizeRef.current = true;
        try { recognition.stop(); } catch (_) {}
      }, 2000);
    };

    const finalizeFromBuffer = () => {
      clearSilenceTimer();
      if (maxDurationTimerRef.current) {
        clearTimeout(maxDurationTimerRef.current);
        maxDurationTimerRef.current = null;
      }
      setIsListening(false);
      const transcript = `${finalTranscriptRef.current} ${lastInterimRef.current}`.trim();
      if (!transcript) {
        setVoiceError("No speech detected — try again");
        return;
      }
      const extracted = extractCatchFromTranscript(transcript);
      setParsed({
        species: extracted.species,
        lengthIn: extracted.lengthIn,
        weightLbs: extracted.weightLbs,
        lure: extracted.lure,
      });
      setText(transcript);
      setPhase("confirming");
    };

    recognition.onresult = (event) => {
      let interim = "";
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const chunk = event.results[index][0]?.transcript || "";
        if (event.results[index].isFinal) {
          finalTranscriptRef.current = `${finalTranscriptRef.current} ${chunk}`.trim();
        } else {
          interim += chunk;
        }
      }

      lastInterimRef.current = interim;
      const composed = `${finalTranscriptRef.current} ${interim}`.trim();
      setText(composed);
      armSilenceTimer();

      if (/\b(that'?s all|done|finish|finished)\b/i.test(composed)) {
        shouldFinalizeRef.current = true;
        try { recognition.stop(); } catch (_) {}
      }
    };

    recognition.onerror = (event) => {
      if (event.error === "aborted") return;
      clearSilenceTimer();
      setIsListening(false);
      setVoiceError("Could not capture voice — please try again");
    };

    recognition.onend = () => {
      if (disposed) return;
      finalizeFromBuffer();
    };

    recognitionRef.current = recognition;
    try {
      recognition.start();
      // Hard 30s cap so a stuck session can't run forever.
      maxDurationTimerRef.current = setTimeout(() => {
        shouldFinalizeRef.current = true;
        try { recognition.stop(); } catch (_) {}
      }, 30000);
    } catch {
      setIsListening(false);
      setVoiceError("Could not start voice capture — please try again");
    }

    return () => {
      disposed = true;
      clearSilenceTimer();
      if (maxDurationTimerRef.current) {
        clearTimeout(maxDurationTimerRef.current);
        maxDurationTimerRef.current = null;
      }
      if (recognitionRef.current === recognition) {
        recognitionRef.current = null;
      }
      try { recognition.stop(); } catch (_) {}
    };
  }, [phase, isSpeechSupported, isListening, isMobileSpeech, isNativeVoice]);

  const handleStartListening = () => {
    if (!isSpeechSupported) {
      setVoiceError("Voice capture unavailable on this browser. Try Chrome or Edge on HTTPS/localhost.");
      return;
    }

    if (isNativeVoice) {
      setVoiceError(null);
      setText("");
      setMicPermissionGranted(true);
      setIsListening(true);
      voice.startDictation().then((ok) => {
        if (!ok) {
          setIsListening(false);
          setVoiceError(voice.error || "Could not start dictation");
        }
      });
      return;
    }

    if (isMobileSpeech) {
      // Must start synchronously within the user gesture on mobile browsers
      setVoiceError(null);
      setMicPermissionGranted(true);
      setIsListening(true);
      finalTranscriptRef.current = "";
      lastInterimRef.current = "";
      shouldFinalizeRef.current = false;

      const SpeechRecognitionCtor = getSpeechRecognitionClass();
      const recognition = new SpeechRecognitionCtor();
      recognition.lang = "en-US";
      recognition.interimResults = false;
      recognition.continuous = false;
      recognition.maxAlternatives = 1;

      let noSpeech = false;
      let hadError = false;

      recognition.onresult = (event) => {
        let transcript = "";
        for (let i = 0; i < event.results.length; i += 1) {
          if (event.results[i].isFinal) {
            transcript += `${event.results[i][0].transcript} `;
          }
        }
        finalTranscriptRef.current = transcript.trim();
        setText(finalTranscriptRef.current);
      };

      recognition.onerror = (event) => {
        if (event.error === "no-speech") { noSpeech = true; return; }
        if (event.error === "aborted") return;
        hadError = true;
        setIsListening(false);
        if (event.error === "not-allowed" || event.error === "service-not-allowed") {
          setVoiceError("Mic access denied — allow microphone in Chrome site settings and try again");
        } else if (event.error === "network") {
          setVoiceError("Network error — check your connection and try again");
        } else if (event.error === "audio-capture") {
          setVoiceError("No microphone found on this device");
        } else {
          setVoiceError(`Voice error (${event.error}) — please try again`);
        }
      };

      recognition.onend = () => {
        if (shouldFinalizeRef.current) return; // cancelled
        if (hadError) return; // onerror already set the message, don't overwrite
        setIsListening(false);
        if (noSpeech) {
          setVoiceError("No speech detected — tap Start Listening and speak clearly");
          return;
        }
        const transcript = finalTranscriptRef.current.trim();
        if (!transcript) {
          setVoiceError("No speech detected — try again");
          return;
        }
        const extracted = extractCatchFromTranscript(transcript);
        setParsed({
          species: extracted.species,
          lengthIn: extracted.lengthIn,
          weightLbs: extracted.weightLbs,
          lure: extracted.lure,
        });
        setText(transcript);
        setPhase("confirming");
      };

      recognitionRef.current = recognition;
      try {
        recognition.start();
      } catch {
        setIsListening(false);
        setVoiceError("Could not start voice capture — please try again");
      }
      return;
    }

    // Desktop: getUserMedia pre-check then trigger useEffect via setIsListening(true)
    const tryStart = async () => {
      try {
        const stream = await navigator.mediaDevices?.getUserMedia?.({ audio: true });
        stream?.getTracks?.().forEach((track) => track.stop());
        setVoiceError(null);
        setMicPermissionGranted(true);
        setIsListening(true);
      } catch {
        setVoiceError("Microphone permission denied or unavailable");
        setMicPermissionGranted(false);
        setIsListening(false);
      }
    };
    tryStart();
  };

  const handleRetryListening = () => {
    setParsed({ species: null, lengthIn: null, weightLbs: null, lure: null });
    setVoiceError(null);
    setSaveError(null);
    shouldFinalizeRef.current = false;
    setIsListening(false);
    setPhase("listening");
  };

  const handleEndLog = () => {
    shouldFinalizeRef.current = true;
    if (isNativeVoice) {
      voice.cancelDictation();
      return;
    }
    recognitionRef.current?.stop();
  };

  const handleCancelCatch = () => {
    shouldFinalizeRef.current = true;
    setIsListening(false);
    if (isNativeVoice) {
      voice.cancelDictation();
    } else {
      recognitionRef.current?.stop();
    }
    onClose();
  };

  const normalizeNumericInput = (value) => String(value ?? "").replace(/\s+/g, "").trim();

  const parseOptionalNumber = (value) => {
    const normalized = normalizeNumericInput(value);
    if (!normalized) return { isEmpty: true, value: 0, valid: true };
    const parsed = Number(normalized);
    if (!Number.isFinite(parsed)) return { isEmpty: false, value: NaN, valid: false };
    return { isEmpty: false, value: parsed, valid: true };
  };

  const validateManualField = (field, snapshot) => {
    const values = snapshot || {
      species: manualSpecies,
      length: manualLength,
      lure: manualLure,
      liveBait: manualLiveBait,
      pounds: manualWeightPounds,
      ounces: manualWeightOunces,
    };
    // Live bait isn't always present in the per-field onChange snapshots, so
    // fall back to current state when computing the lure/live-bait rule.
    const liveBaitValue = values.liveBait != null ? values.liveBait : manualLiveBait;

    if (field === "species") {
      if (!values.species) return "Choose a species.";
      if (!isSpeciesAllowed(values.species)) {
        return `Only ${allowedSpecies.join(", ")} can be logged in this tournament.`;
      }
      return "";
    }
    if (field === "length") {
      const normalized = normalizeNumericInput(values.length);
      if (!normalized) return "Enter a length between 0 and 60 inches.";
      const parsed = Number(normalized);
      if (!Number.isFinite(parsed) || parsed < 0 || parsed > 60) {
        return "Length must be between 0 and 60 inches.";
      }
      return "";
    }
    if (field === "lure") {
      if (values.lure || liveBaitValue) return "";
      return "Choose a lure or live bait.";
    }
    if (field === "pounds") {
      const parsed = parseOptionalNumber(values.pounds);
      if (!parsed.valid || parsed.value < 0) {
        return "Pounds must be 0 or greater.";
      }
      return "";
    }
    if (field === "ounces") {
      const parsed = parseOptionalNumber(values.ounces);
      if (!parsed.valid || parsed.value < 0 || parsed.value > 11) {
        return "Ounces must be between 0 and 11.";
      }
      return "";
    }
    return "";
  };

  const validateManualForm = () => {
    const snapshot = {
      species: manualSpecies,
      length: manualLength,
      lure: manualLure,
      liveBait: manualLiveBait,
      pounds: manualWeightPounds,
      ounces: manualWeightOunces,
    };
    // Missed fish only require species + a lure/bait — there is no landed fish
    // to measure, so length and weight are skipped entirely.
    const nextErrors = {
      species: validateManualField("species", snapshot),
      length: isMissed ? "" : validateManualField("length", snapshot),
      lure: validateManualField("lure", snapshot),
      pounds: isMissed ? "" : validateManualField("pounds", snapshot),
      ounces: isMissed ? "" : validateManualField("ounces", snapshot),
    };
    setManualFieldErrors(nextErrors);
    return !Object.values(nextErrors).some(Boolean);
  };

  // Shared handlers for the themed numeric inputs (length / pounds / ounces).
  const numberSetters = {
    length: setManualLength,
    pounds: setManualWeightPounds,
    ounces: setManualWeightOunces,
  };
  const numberValues = {
    length: manualLength,
    pounds: manualWeightPounds,
    ounces: manualWeightOunces,
  };
  const handleManualNumberChange = (field, rawValue) => {
    const nextValue = normalizeNumericInput(rawValue);
    numberSetters[field](nextValue);
    setManualFieldErrors((prev) => {
      if (!prev[field]) return prev;
      return {
        ...prev,
        [field]: validateManualField(field, {
          species: manualSpecies,
          length: field === "length" ? nextValue : manualLength,
          lure: manualLure,
          liveBait: manualLiveBait,
          pounds: field === "pounds" ? nextValue : manualWeightPounds,
          ounces: field === "ounces" ? nextValue : manualWeightOunces,
        }),
      };
    });
  };
  const handleManualNumberBlur = (field) => {
    setManualFieldErrors((prev) => ({ ...prev, [field]: validateManualField(field) }));
  };
  const handleManualNumberStep = (field, dir, opts) => {
    handleManualNumberChange(field, stepNumber(numberValues[field], dir, opts));
  };

  const handleSaveManualCatch = async () => {
    if (!validateManualForm()) {
      return;
    }

    // Missed fish carry no length/weight — force them null regardless of any
    // stale values left in the inputs.
    const parsedLength = isMissed ? null : Number(normalizeNumericInput(manualLength));
    const parsedPounds = parseOptionalNumber(manualWeightPounds).value;
    const parsedOunces = parseOptionalNumber(manualWeightOunces).value;

    const manualWeightLbs = isMissed ? null : parsedPounds + parsedOunces / 16;

    setSaving(true);
    setSaveError(null);
    try {
      const catchId = await logCatch(
        {
          outcome: isMissed ? "missed" : "landed",
          species: manualSpecies,
          lengthIn: parsedLength,
          weightLbs: manualWeightLbs,
          lure: manualLure || null,
          liveBait: manualLiveBait || null,
          waterClarity: waterClarity || null,
          transcript: null,
        },
        catchConditions,
        location
      );
      if (!catchId) {
        throw new Error("Catch ID was not returned");
      }
      if (!isMissed) {
        await backfillCert(catchId, sessionId, {
          species: manualSpecies,
          lengthIn: parsedLength,
          weightLbs: manualWeightLbs,
          lure: manualLure || null,
          liveBait: manualLiveBait || null,
        });
        emitSaved(catchId, sessionId, {
          species: manualSpecies,
          lengthIn: parsedLength,
          weightLbs: manualWeightLbs,
          lure: manualLure || null,
        });
      }
      setPhase("done");
      setTimeout(onClose, 900);
    } catch (err) {
      console.error("Manual catch save failed:", err);
      setSaveError("Save failed — catch not recorded");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: C.settingsBg,
        color: C.white,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        zIndex: 20,
        overflowY: "auto",
      }}
    >
      {isTournamentSpeciesRestricted && phase !== "done" && (
        <div
          style={{
            width: "100%",
            maxWidth: 320,
            marginBottom: 12,
            padding: "8px 12px",
            borderRadius: 8,
            border: `1px solid ${C.seafoam}55`,
            background: `${C.seafoam}14`,
            color: C.seafoam,
            fontSize: 11,
            textAlign: "center",
            lineHeight: 1.4,
          }}
        >
          <span style={{ marginRight: 6 }}>ⓘ</span>
          Tournament species: {allowedSpecies.join(", ")}
          {tournamentName ? ` (${tournamentName})` : ""}
        </div>
      )}
      {phase !== "done" && isHarness && (
        <div
          id="catch-trial-picks"
          data-testid="catch-trial-picks"
          style={{
            width: "100%",
            maxWidth: 320,
            marginBottom: 12,
            padding: "8px 12px",
            borderRadius: 8,
            border: `1px solid ${C.border}`,
            background: `${C.cardBg}`,
            color: C.white,
            fontSize: 11,
            lineHeight: 1.45,
          }}
        >
          <div style={{ fontSize: 9, color: C.muted, letterSpacing: 1.2, marginBottom: 4 }}>
            TRIAL PICKS FROM ENROLLMENT
          </div>
          <div>Preferred species: {trialSpeciesLabel || "—"}</div>
          <div>Preferred lure: {trialLureLabel || "—"}</div>
          <div>Preferred live bait: {trialLiveBaitLabel || "—"}</div>
        </div>
      )}
      {phase === "listening" && (
        <>
          {isMissed && (
            <div style={{ textAlign: "center", marginBottom: 8 }}>
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  background: `${C.warning}18`,
                  border: `2px solid ${C.warning}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto",
                  boxShadow: `0 0 24px ${C.warning}33`,
                }}
              >
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M2 12s3.5-5 9-5c3.6 0 6.5 2 8 4l3-2v6l-3-2c-1.5 2-4.4 4-8 4-5.5 0-9-5-9-5z"
                    stroke={C.warning}
                    strokeWidth="1.6"
                    strokeLinejoin="round"
                    strokeLinecap="round"
                  />
                  <circle cx="7" cy="11" r="1" fill={C.warning} />
                </svg>
              </div>
              <div style={{ fontSize: 11, color: C.warning, letterSpacing: 3, marginTop: 10 }}>
                MISSED FISH
              </div>
            </div>
          )}
          {!isMissed && (canVoiceLog ? (
          <>
          <div
            style={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              background: `${C.gold}22`,
              border: `2px solid ${C.gold}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 24,
              boxShadow: `0 0 30px ${C.gold}40`,
              fontSize: 32,
            }}
          >
            🎙
          </div>
          <div style={{ fontSize: 11, color: C.gold, letterSpacing: 3, marginBottom: 16 }}>
            {isListening ? "LISTENING…" : "READY TO LISTEN"}
          </div>
          <VoiceWave active={isListening} />
          <div
            style={{
              marginTop: 24,
              padding: "14px 18px",
              background: C.cardBg,
              borderRadius: 10,
              border: `1px solid ${C.gold}44`,
              maxWidth: 280,
              minHeight: 60,
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontSize: 13,
                color: C.sand,
                fontFamily: "Georgia,serif",
                fontStyle: "italic",
                lineHeight: 1.6,
              }}
            >
              {text || <span style={{ color: C.muted }}>Say: species, length, weight, lure…</span>}
            </div>
          </div>
          <div style={{ marginTop: 20, fontSize: 11, color: C.muted }}>
            Say "that's all" to finish
          </div>
          {!isListening ? (
            <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8 }}>
              <Button
                variant="secondary"
                size="sm"
                id="catch-start-listening"
                data-testid="catch-start-listening"
                onClick={handleStartListening}
              >
                Start Listening
              </Button>
              <span
                style={{
                  fontSize: 9,
                  color: micPermissionGranted ? C.seafoam : C.muted,
                  letterSpacing: 0.5,
                }}
              >
                {micPermissionGranted ? "● mic granted" : "○ mic not granted"}
              </span>
            </div>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              onClick={handleEndLog}
              style={{ marginTop: 10 }}
            >
              End Log
            </Button>
          )}
          </>
          ) : (
            <div
              id="catch-voice-locked"
              data-testid="catch-voice-locked"
              style={{
                width: "100%",
                maxWidth: 300,
                marginTop: 8,
                marginBottom: 4,
                padding: "12px 14px",
                borderRadius: 10,
                border: `1px solid ${C.gold}44`,
                background: `${C.gold}08`,
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: 13, color: C.sand, fontWeight: 600, lineHeight: 1.4, marginBottom: 6 }}>
                Voice catch logging is a Pro feature.
              </div>
              <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
                Log catches hands-free by voice with Pro. For now, enter your catch below.
              </div>
            </div>
          ))}
          {!isListening && (
            <div style={{ marginTop: 14, width: "100%", maxWidth: 300 }}>
              <CollapsibleSection
                open={showManualEntry}
                onToggle={() => setShowManualEntry(!showManualEntry)}
                headerTitle={showManualEntry ? "Hide manual entry" : "Show manual entry"}
                title={<span style={{ fontSize: 10, color: C.muted, letterSpacing: 2 }}>MANUAL CATCH ENTRY</span>}
                glow={`${C.seafoam}44`}
              >
                  <div style={{ display: "grid", gap: 8 }}>
                    {/* Landed vs Missed — a missed fish is a bite/strike that
                        got away. It reuses species/lure/bait/clarity but has no
                        length or weight. */}
                    <div
                      role="group"
                      aria-label="Catch outcome"
                      style={{
                        display: "flex",
                        gap: 4,
                        padding: 3,
                        borderRadius: 10,
                        border: `1px solid ${C.border}`,
                        background: C.cardBg,
                      }}
                    >
                      {[
                        { key: "landed", label: "Landed" },
                        { key: "missed", label: "Missed" },
                      ].map((opt) => {
                        const active = catchOutcome === opt.key;
                        return (
                          <button
                            key={opt.key}
                            type="button"
                            data-testid={`catch-outcome-${opt.key}`}
                            data-selected={active ? "true" : "false"}
                            onClick={() => {
                              setCatchOutcome(opt.key);
                              // Clear any length/weight errors when switching to
                              // missed — those fields no longer apply.
                              if (opt.key === "missed") {
                                setManualFieldErrors((prev) => ({ ...prev, length: "", pounds: "", ounces: "" }));
                              }
                            }}
                            style={{
                              flex: 1,
                              padding: "7px 0",
                              borderRadius: 8,
                              border: "none",
                              cursor: "pointer",
                              fontSize: 11,
                              fontWeight: 700,
                              letterSpacing: 0.5,
                              background: active ? (opt.key === "missed" ? `${C.warning}22` : `${C.seafoam}22`) : "transparent",
                              color: active ? (opt.key === "missed" ? C.warning : C.seafoam) : C.muted,
                              boxShadow: active ? `inset 0 0 0 1px ${opt.key === "missed" ? C.warning : C.seafoam}66` : "none",
                            }}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                    {isMissed && (
                      <div style={{ fontSize: 9, color: C.muted, lineHeight: 1.4, marginTop: -2 }}>
                        Logging a fish that got away — length & weight are skipped.
                      </div>
                    )}
                    <label style={{ display: "grid", gap: 4 }}>
                      <span style={{ fontSize: 9, color: C.muted, letterSpacing: 1 }}>Species</span>
                      <select
                        id="catch-manual-species"
                        data-testid="catch-manual-species"
                        value={manualSpecies}
                        onChange={(event) => {
                          const nextValue = event.target.value;
                          setManualSpecies(nextValue);
                          if (manualFieldErrors.species) {
                            setManualFieldErrors((prev) => ({
                              ...prev,
                              species: validateManualField("species", {
                                species: nextValue,
                                length: manualLength,
                                lure: manualLure,
                                pounds: manualWeightPounds,
                                ounces: manualWeightOunces,
                              }),
                            }));
                          }
                        }}
                        onBlur={() => {
                          setManualFieldErrors((prev) => ({
                            ...prev,
                            species: validateManualField("species"),
                          }));
                        }}
                        style={{
                          height: 30,
                          borderRadius: 8,
                          border: `1px solid ${manualFieldErrors.species ? C.red : C.border}`,
                          background: C.cardBg,
                          color: C.white,
                          padding: "0 8px",
                          fontSize: 11,
                        }}
                      >
                        <option value="">Select species</option>
                        {speciesOptions.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                      {manualFieldErrors.species && (
                        <span style={{ fontSize: 9, color: C.red }}>{manualFieldErrors.species}</span>
                      )}
                    </label>

                    {!isMissed && (
                    <label style={{ display: "grid", gap: 4 }}>
                      <span style={{ fontSize: 9, color: C.muted, letterSpacing: 1 }}>Length</span>
                      <ThemedNumberInput
                        id="catch-manual-length"
                        testId="catch-manual-length"
                        ariaLabel="Length in inches"
                        value={manualLength}
                        min={0}
                        max={60}
                        step={0.1}
                        suffix={"\""}
                        hasError={!!manualFieldErrors.length}
                        onChange={(raw) => handleManualNumberChange("length", raw)}
                        onBlur={() => handleManualNumberBlur("length")}
                        onStep={(dir) => handleManualNumberStep("length", dir, { min: 0, max: 60, step: 0.1 })}
                      />
                      {manualFieldErrors.length && (
                        <span style={{ fontSize: 9, color: C.red }}>{manualFieldErrors.length}</span>
                      )}
                    </label>
                    )}

                    {lureOptions.length > 0 && (
                      <label style={{ display: "grid", gap: 4 }}>
                        <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <span style={{ fontSize: 9, color: C.muted, letterSpacing: 1 }}>Lure</span>
                          {manualLure && (
                            <button
                              type="button"
                              data-testid="catch-manual-lure-clear"
                              onClick={() => {
                                baitTouchedRef.current = true;
                                setManualLure("");
                              }}
                              style={{
                                background: "transparent",
                                border: "none",
                                color: C.seafoam,
                                cursor: "pointer",
                                fontSize: 9,
                                fontWeight: 700,
                                letterSpacing: 0.5,
                                padding: 0,
                              }}
                            >
                              ✕ Clear
                            </button>
                          )}
                        </span>
                        <select
                          id="catch-manual-lure"
                          data-testid="catch-manual-lure"
                          value={manualLure}
                          disabled={!!manualLiveBait}
                          onChange={(event) => {
                            const nextValue = event.target.value;
                            baitTouchedRef.current = true;
                            setManualLure(nextValue);
                            // Mutually exclusive with live bait — picking a
                            // lure clears any live-bait selection.
                            if (nextValue) setManualLiveBait("");
                            if (manualFieldErrors.lure) {
                              setManualFieldErrors((prev) => ({
                                ...prev,
                                lure: validateManualField("lure", {
                                  species: manualSpecies,
                                  length: manualLength,
                                  lure: nextValue,
                                  liveBait: nextValue ? "" : manualLiveBait,
                                  pounds: manualWeightPounds,
                                  ounces: manualWeightOunces,
                                }),
                              }));
                            }
                          }}
                          onBlur={() => {
                            setManualFieldErrors((prev) => ({
                              ...prev,
                              lure: validateManualField("lure"),
                            }));
                          }}
                          style={{
                            height: 30,
                            borderRadius: 8,
                            border: `1px solid ${manualFieldErrors.lure ? C.red : C.border}`,
                            background: C.cardBg,
                            color: C.white,
                            padding: "0 8px",
                            fontSize: 11,
                            opacity: manualLiveBait ? 0.45 : 1,
                            cursor: manualLiveBait ? "not-allowed" : "pointer",
                          }}
                        >
                          <option value="">Select lure</option>
                          {lureOptions.map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      </label>
                    )}

                    {liveBaitOptions.length > 0 && (
                      <label style={{ display: "grid", gap: 4 }}>
                        <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <span style={{ fontSize: 9, color: C.muted, letterSpacing: 1 }}>Live Bait</span>
                          {manualLiveBait && (
                            <button
                              type="button"
                              data-testid="catch-manual-livebait-clear"
                              onClick={() => {
                                baitTouchedRef.current = true;
                                setManualLiveBait("");
                              }}
                              style={{
                                background: "transparent",
                                border: "none",
                                color: C.seafoam,
                                cursor: "pointer",
                                fontSize: 9,
                                fontWeight: 700,
                                letterSpacing: 0.5,
                                padding: 0,
                              }}
                            >
                              ✕ Clear
                            </button>
                          )}
                        </span>
                        <select
                          id="catch-manual-livebait"
                          data-testid="catch-manual-livebait"
                          value={manualLiveBait}
                          disabled={!!manualLure}
                          onChange={(event) => {
                            const nextValue = event.target.value;
                            baitTouchedRef.current = true;
                            setManualLiveBait(nextValue);
                            // Mutually exclusive with lure — picking a live
                            // bait clears any lure selection.
                            if (nextValue) setManualLure("");
                            if (manualFieldErrors.lure) {
                              setManualFieldErrors((prev) => ({
                                ...prev,
                                lure: validateManualField("lure", {
                                  species: manualSpecies,
                                  length: manualLength,
                                  lure: nextValue ? "" : manualLure,
                                  liveBait: nextValue,
                                  pounds: manualWeightPounds,
                                  ounces: manualWeightOunces,
                                }),
                              }));
                            }
                          }}
                          style={{
                            height: 30,
                            borderRadius: 8,
                            border: `1px solid ${manualFieldErrors.lure ? C.red : C.border}`,
                            background: C.cardBg,
                            color: C.white,
                            padding: "0 8px",
                            fontSize: 11,
                            opacity: manualLure ? 0.45 : 1,
                            cursor: manualLure ? "not-allowed" : "pointer",
                          }}
                        >
                          <option value="">Select live bait</option>
                          {liveBaitOptions.map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      </label>
                    )}
                    {manualFieldErrors.lure && (
                      <span style={{ fontSize: 9, color: C.red }}>{manualFieldErrors.lure}</span>
                    )}

                    <label style={{ display: "grid", gap: 4 }}>
                      <span style={{ fontSize: 9, color: C.muted, letterSpacing: 1 }}>Water Clarity (optional)</span>
                      <select
                        id="catch-manual-water-clarity"
                        data-testid="catch-manual-water-clarity"
                        value={waterClarity}
                        onChange={(event) => setWaterClarityTouched(event.target.value)}
                        style={{
                          height: 30,
                          borderRadius: 8,
                          border: `1px solid ${C.border}`,
                          background: C.cardBg,
                          color: C.white,
                          padding: "0 8px",
                          fontSize: 11,
                        }}
                      >
                        <option value="">Select water clarity</option>
                        {waterClarityOptions.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                      {suggestedClarity && (
                        <span style={{ fontSize: 9, color: C.seafoam }}>
                          Suggested from water turbidity{typeof flow.turbidity === "number" ? ` (${Math.round(flow.turbidity)} FNU)` : ""}
                        </span>
                      )}
                    </label>

                    {!isMissed && (
                    <div style={{ display: "grid", gap: 4 }}>
                      <span style={{ fontSize: 9, color: C.muted, letterSpacing: 1 }}>Weight</span>
                      <div style={{ display: "flex", gap: 8 }}>
                        <label style={{ flex: 1, display: "grid", gap: 4 }}>
                          <span style={{ fontSize: 9, color: C.muted }}>Pounds</span>
                          <ThemedNumberInput
                            id="catch-manual-pounds"
                            testId="catch-manual-pounds"
                            ariaLabel="Weight in pounds"
                            value={manualWeightPounds}
                            min={0}
                            step={1}
                            hasError={!!manualFieldErrors.pounds}
                            onChange={(raw) => handleManualNumberChange("pounds", raw)}
                            onBlur={() => handleManualNumberBlur("pounds")}
                            onStep={(dir) => handleManualNumberStep("pounds", dir, { min: 0, step: 1 })}
                          />
                          {manualFieldErrors.pounds && (
                            <span style={{ fontSize: 9, color: C.red }}>{manualFieldErrors.pounds}</span>
                          )}
                        </label>
                        <label style={{ flex: 1, display: "grid", gap: 4 }}>
                          <span style={{ fontSize: 9, color: C.muted }}>Ounces</span>
                          <ThemedNumberInput
                            id="catch-manual-ounces"
                            testId="catch-manual-ounces"
                            ariaLabel="Weight in ounces"
                            value={manualWeightOunces}
                            min={0}
                            max={11}
                            step={1}
                            hasError={!!manualFieldErrors.ounces}
                            onChange={(raw) => handleManualNumberChange("ounces", raw)}
                            onBlur={() => handleManualNumberBlur("ounces")}
                            onStep={(dir) => handleManualNumberStep("ounces", dir, { min: 0, max: 11, step: 1 })}
                          />
                          {manualFieldErrors.ounces && (
                            <span style={{ fontSize: 9, color: C.red }}>{manualFieldErrors.ounces}</span>
                          )}
                        </label>
                      </div>
                    </div>
                    )}

                    <Button
                      variant="primary"
                      fullWidth
                      id="catch-save-manual"
                      data-testid="catch-save-manual"
                      onClick={handleSaveManualCatch}
                      disabled={saving}
                      style={{ marginTop: 2 }}
                    >
                      {saving ? "Saving…" : isMissed ? "Save Missed Fish" : "Save Catch"}
                    </Button>
                  </div>
              </CollapsibleSection>
            </div>
          )}
          {voiceError && (
            <div style={{ marginTop: 10, fontSize: 10, color: C.red, textAlign: "center" }}>
              {voiceError}
            </div>
          )}
          {voiceError && (
            <Button
              variant="secondary"
              size="sm"
              onClick={handleRetryListening}
              style={{ marginTop: 12 }}
            >
              Retry voice capture
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            id="catch-cancel"
            data-testid="catch-cancel"
            onClick={handleCancelCatch}
            style={{ marginTop: 10 }}
          >
            Cancel
          </Button>
        </>
      )}

      {phase === "confirming" && (
        <>
          <div style={{ fontSize: 11, color: C.accent, letterSpacing: 3, marginBottom: 20 }}>
            AI EXTRACTED — CONFIRM?
          </div>
          <Card
            style={{
              width: "100%",
              maxWidth: 300,
              padding: "16px 18px",
              marginBottom: 20,
              borderColor: C.accent + "66",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: `1px solid ${C.border}` }}>
              <span style={{ fontSize: 11, color: C.muted, textTransform: "capitalize" }}>species</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: C.white, fontFamily: "Georgia,serif" }}>
                {parsed.species || "—"}
              </span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: `1px solid ${C.border}` }}>
              <span style={{ fontSize: 11, color: C.muted, textTransform: "capitalize" }}>length</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: C.white, fontFamily: "Georgia,serif" }}>
                {formatLength(parsed.lengthIn)}
              </span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: `1px solid ${C.border}` }}>
              <span style={{ fontSize: 11, color: C.muted, textTransform: "capitalize" }}>weight</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: C.white, fontFamily: "Georgia,serif" }}>
                {formatWeight(parsed.weightLbs)}
              </span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: `1px solid ${C.border}` }}>
              <span style={{ fontSize: 11, color: C.muted, textTransform: "capitalize" }}>lure</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: C.white, fontFamily: "Georgia,serif" }}>
                {parsed.lure || "—"}
              </span>
            </div>
            <div style={{ display: "grid", gap: 4, padding: "7px 0", borderBottom: `1px solid ${C.border}` }}>
              <span style={{ fontSize: 11, color: C.muted, textTransform: "capitalize" }}>water clarity</span>
              <select
                value={waterClarity}
                onChange={(event) => setWaterClarityTouched(event.target.value)}
                style={{
                  height: 30,
                  borderRadius: 8,
                  border: `1px solid ${C.border}`,
                  background: C.cardBg,
                  color: C.white,
                  padding: "0 8px",
                  fontSize: 11,
                }}
              >
                <option value="">Select water clarity</option>
                {waterClarityOptions.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
              {suggestedClarity && (
                <span style={{ fontSize: 9, color: C.seafoam }}>
                  Suggested from water turbidity{typeof flow.turbidity === "number" ? ` (${Math.round(flow.turbidity)} FNU)` : ""}
                </span>
              )}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "7px 0" }}>
              <span style={{ fontSize: 11, color: C.muted }}>GPS</span>
              <span style={{ fontSize: 11, color: C.seafoam }}>{formatGps(location)}</span>
            </div>
          </Card>
          <div style={{ display: "flex", gap: 10, width: "100%", maxWidth: 300 }}>
            <Button
              variant="primary"
              id="catch-confirm"
              data-testid="catch-confirm"
              onClick={async () => {
                if (isTournamentSpeciesRestricted && !isSpeciesAllowed(parsed.species)) {
                  setSaveError(
                    `Only ${allowedSpecies.join(", ")} can be logged in this tournament. Tap Edit to choose an allowed species.`
                  );
                  return;
                }
                setSaving(true);
                setSaveError(null);
                try {
                  const catchId = await logCatch(
                    {
                      species: parsed.species,
                      lengthIn: parsed.lengthIn,
                      weightLbs: parsed.weightLbs,
                      lure: parsed.lure,
                      waterClarity: waterClarity || null,
                      transcript: text,
                    },
                    catchConditions,
                    location
                  );
                  if (!catchId) {
                    throw new Error("Catch ID was not returned");
                  }
                  await backfillCert(catchId, sessionId, {
                    species: parsed.species,
                    lengthIn: parsed.lengthIn,
                    weightLbs: parsed.weightLbs,
                    lure: parsed.lure,
                  });
                  emitSaved(catchId, sessionId, {
                    species: parsed.species,
                    lengthIn: parsed.lengthIn,
                    weightLbs: parsed.weightLbs,
                    lure: parsed.lure,
                  });
                  setPhase("done");
                  setTimeout(onClose, 900);
                } catch (err) {
                  console.error("Catch save failed:", err);
                  setSaveError("Save failed — catch not recorded");
                } finally {
                  setSaving(false);
                }
              }}
              disabled={saving}
              style={{ flex: 2 }}
            >
              {saving ? "Saving…" : "✓ Confirm"}
            </Button>
            <Button
              variant="ghost"
              onClick={handleRetryListening}
              style={{ flex: 1 }}
            >
              Edit
            </Button>
          </div>
          {saveError && (
            <div style={{ marginTop: 10, fontSize: 10, color: C.red, textAlign: "center" }}>
              {saveError}
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            id="catch-cancel-confirm"
            data-testid="catch-cancel-confirm"
            onClick={handleCancelCatch}
            style={{ marginTop: 10 }}
          >
            Cancel
          </Button>
          <div style={{ marginTop: 16, fontSize: 11, color: C.muted }}>
            Or say "confirm" / "edit"
          </div>
        </>
      )}

      {phase === "done" && (
        <>
          <div
            style={{
              width: 70,
              height: 70,
              borderRadius: "50%",
              background: `${C.seafoam}22`,
              border: `2px solid ${C.seafoam}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 16,
            }}
          >
            <span style={{ fontSize: 28, color: C.accent }}>✓</span>
          </div>
          <div
            style={{
              fontSize: 16,
              color: C.accent,
              fontFamily: "Georgia,serif",
              fontWeight: 700,
            }}
          >
            Catch Logged
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>
            Returning to recording…
          </div>
        </>
      )}
    </div>
  );
}
